import React, { useState } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select,
  Button, HStack, Box, Text, useToast, Tag,
} from '@chakra-ui/react';
import { ImmCita, crearCita } from '../../../api/immApi';
import {
  SectionTitle, Field, ExpedienteSelect, IMM_COLOR, IMM_COLOR_HOVER, fmtDateTime,
} from '../_shared';

type Modo = 'new' | 'view';

interface Props {
  modo: Modo;
  data?: ImmCita;
  onGuardado?: (c: ImmCita) => void;
  onCancelar?: () => void;
  defaultFecha?: string;
}

const SLOTS = ['09:00', '10:00', '11:00', '12:00', '13:00', '16:00', '17:00', '18:00'];
const hoy = () => new Date().toISOString().split('T')[0];

const FormularioCita: React.FC<Props> = ({ modo, data, onGuardado, onCancelar, defaultFecha }) => {
  const toast = useToast();
  const [guardando, setGuardando] = useState(false);
  const [expedienteId, setExpedienteId] = useState<number | undefined>(data?.expedienteId ?? undefined);

  const [f, setF] = useState<Partial<ImmCita>>({
    fecha: data?.fecha ? data.fecha.split('T')[0] : (defaultFecha || hoy()),
    horaInicio: data?.horaInicio || '',
    horaFin: data?.horaFin || '',
    psicologo: data?.psicologo || '',
    motivo: data?.motivo || '',
    origen: data?.origen || 'IMM',
  });

  const set = (campo: keyof ImmCita, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  const handleGuardar = async () => {
    if (!f.fecha) {
      toast({ title: 'La fecha es obligatoria', status: 'warning', duration: 3000 });
      return;
    }
    if (!f.horaInicio) {
      toast({ title: 'La hora de inicio es obligatoria', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmCita> = { ...f, expedienteId: expedienteId ?? null };
      const res = await crearCita(payload);
      toast({ title: 'Cita creada', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar la cita.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view' && data) {
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos de la cita</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Expediente" value={data.expedienteId ? '#' + data.expedienteId : 'Sin expediente'} />
          <Field label="Fecha" value={data.fecha ? data.fecha.split('T')[0] : '—'} />
          <Field label="Horario" value={`${data.horaInicio || '—'}${data.horaFin ? ' - ' + data.horaFin : ''}`} />
          <Field label="Psicólogo/a" value={data.psicologo} />
          <Field label="Motivo" value={data.motivo} />
          <Field label="Origen" value={data.origen} />
          <Field label="Estatus" value={data.estatus} />
        </SimpleGrid>

        {data.sesiones && data.sesiones.length > 0 && (
          <>
            <SectionTitle>Sesiones registradas</SectionTitle>
            <VStack spacing={2} align="stretch">
              {data.sesiones.map((s) => (
                <Box key={s.id} borderWidth="1px" borderRadius="md" p={3} borderLeft={`3px solid ${IMM_COLOR}`}>
                  <HStack justify="space-between" mb={1}>
                    <Text fontSize="xs" color="gray.500">{fmtDateTime(s.createdAt)}</Text>
                    {s.confidencial && <Tag size="sm" colorScheme="red" variant="subtle">Confidencial</Tag>}
                  </HStack>
                  <Text fontSize="sm" whiteSpace="pre-wrap">{s.notasSeguimiento || '—'}</Text>
                </Box>
              ))}
            </VStack>
          </>
        )}

        <HStack justify="flex-end" pt={2}>
          <Button variant="outline" onClick={onCancelar}>Cerrar</Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos de la cita</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl gridColumn={{ md: 'span 3' }}>
          <FormLabel fontSize="sm">Expediente (opcional)</FormLabel>
          <ExpedienteSelect value={expedienteId} onChange={setExpedienteId} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={(f.fecha as string) || ''} onChange={(e) => set('fecha', e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Hora de inicio</FormLabel>
          <Select placeholder="Selecciona" value={f.horaInicio || ''} onChange={(e) => set('horaInicio', e.target.value)}>
            {SLOTS.map((h) => <option key={h} value={h}>{h}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Hora de fin</FormLabel>
          <Input type="time" value={f.horaFin || ''} onChange={(e) => set('horaFin', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Psicólogo/a</FormLabel>
          <Input value={f.psicologo || ''} onChange={(e) => set('psicologo', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Origen</FormLabel>
          <Select value={f.origen || 'IMM'} onChange={(e) => set('origen', e.target.value)}>
            <option value="IMM">IMM</option>
            <option value="DIF">DIF</option>
          </Select>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 3' }}>
          <FormLabel fontSize="sm">Motivo</FormLabel>
          <Input value={f.motivo || ''} onChange={(e) => set('motivo', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar cita
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioCita;
